<template>
  <section class="br-form-section" :class="{ 'br-form-section--compact': compact }">
    <header v-if="title || description" class="br-form-section__header">
      <div class="br-form-section__heading">
        <div v-if="icon" class="br-form-section__icon-wrap">
          <v-icon :color="iconColor" size="20" class="br-form-section__icon">
            {{ icon }}
          </v-icon>
        </div>
        <div>
          <h3 v-if="title" class="br-form-section__title">{{ title }}</h3>
          <p v-if="description" class="br-form-section__description">{{ description }}</p>
        </div>
      </div>
      <div v-if="$slots.headerActions" class="br-form-section__header-actions">
        <slot name="headerActions" />
      </div>
    </header>
    <div class="br-form-section__body">
      <slot />
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "FormSection",

  props: {
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    icon: { type: String, default: "" },
    iconColor: { type: String, default: "primary" },
    compact: { type: Boolean, default: false },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.br-form-section {
  margin-bottom: $br-space-6;
  padding: $br-space-5;
  border-radius: $br-radius-xl;
  border: 1px solid $br-border-soft;
  background: linear-gradient(180deg, $br-bg-elevated 0%, $br-bg-subtle 160%);
  box-shadow: $br-shadow-sm;
}

.br-form-section--compact {
  padding: $br-space-4;
  margin-bottom: $br-space-4;
}

.br-form-section__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: $br-space-3;
  margin-bottom: $br-space-4;
  padding-bottom: $br-space-3;
  border-bottom: 1px solid $br-border-soft;
}

.br-form-section__heading {
  display: flex;
  align-items: flex-start;
  gap: $br-space-3;
  min-width: 0;
}

.br-form-section__icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(145deg, rgba(229, 24, 61, 0.14), rgba(229, 24, 61, 0.05));
  box-shadow: inset 0 0 0 1px rgba(229, 24, 61, 0.08);
}

.br-form-section__icon {
  flex-shrink: 0;
}

.br-form-section__title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: $br-ink-soft;
  line-height: 1.3;
  letter-spacing: -0.1px;
}

.br-form-section__description {
  margin: $br-space-1 0 0;
  font-size: $br-font-size-sm;
  color: $br-muted;
  line-height: 1.45;
  max-width: 56ch;
}

.br-form-section__header-actions {
  display: flex;
  align-items: center;
  gap: $br-space-2;
  flex-shrink: 0;
}

.br-form-section__body ::v-deep .v-input--outlined .v-input__slot {
  border-radius: $br-radius-sm !important;
}
</style>
